import { AbstractDate } from "@linkfluence/models/date";
import { Clause } from "@radarly/models/clause";
import {
  refineClauseWithInnerClause,
  refineClauseWithoutField,
} from "@radarly/models/clause/refiner";
import {
  Dataviz,
  DatavizName,
  getDefaultDataviz,
  isPictureWallDataviz,
} from "@radarly/models/dataviz";
import { Card, PostsCard } from "..";
import { SearchFragmentPosts, Sort } from "@radarly/models/search";
import { isPostsCardDataviz } from "../dataviz";
import { CardFactory, CardFragmentsFactory } from "./base";
import { ICardFactory } from "./model";

interface PostsCardFactoryOptions {
  clause?: Clause;
  sortBy?: Sort["on"];
  from?: AbstractDate;
  to?: AbstractDate;
}

export class PostsCardFragmentsFactory extends CardFragmentsFactory {
  static create(input?: PostsCardFactoryOptions) {
    const factory = new PostsCardFragmentsFactory();
    return factory.getFragments(input);
  }
  static createFromCard(card: Card, input?: PostsCardFactoryOptions) {
    const factory = new PostsCardFragmentsFactory();
    const { from, to } = factory.getPeriod(card);
    return factory.getFragments({
      clause: factory.getClauseFromCard(card),
      from,
      to,
      sortBy: input?.sortBy, // @TODO
    });
  }
  private getFragments(input?: PostsCardFactoryOptions): [SearchFragmentPosts] {
    return [this.getFragment(input)];
  }
  private getFragment(input?: PostsCardFactoryOptions): SearchFragmentPosts {
    return {
      request: {
        query: this.getQuery(input),
        pagination: {
          limit: 200,
          start: 0,
          sort: {
            on: input?.sortBy || "date",
            order: "desc",
          },
        },
      },
    };
  }
}
export class PostsCardFactory
  implements ICardFactory<PostsCard, PostsCardFactoryOptions>
{
  public static getInstance() {
    return new PostsCardFactory();
  }
  public fromCard(
    workspaceId: number,
    projectFocuses: number[],
    card: Card,
    input?: PostsCardFactoryOptions & {
      dataviz?: PostsCard["dataviz"];
    }
  ) {
    const fragments = PostsCardFragmentsFactory.createFromCard(card, input);
    const dataviz = this.createDataviz(input?.dataviz || card.dataviz);
    return CardFactory.getInstance<PostsCard>().createCard(
      workspaceId,
      projectFocuses,
      "posts",
      card.title,
      isPictureWallDataviz(dataviz)
        ? [this.getFragmentWithPicturesContext(fragments[0])]
        : [this.getFragmentWithoutPicturesContext(fragments[0])],
      dataviz
    );
  }
  public fromInput(
    workspaceId: number,
    projectFocuses: number[],
    title: string,
    input?: PostsCardFactoryOptions & {
      dataviz?: Dataviz["name"];
      fullDataviz?: Dataviz;
    }
  ) {
    const fragments = PostsCardFragmentsFactory.create(input);
    const dataviz = this.createDataviz(
      input?.fullDataviz ??
        getDefaultDataviz(input?.dataviz || DatavizName.TopPosts)
    );
    return CardFactory.getInstance<PostsCard>().createCard(
      workspaceId,
      projectFocuses,
      "posts",
      title,
      isPictureWallDataviz(dataviz)
        ? [this.getFragmentWithPicturesContext(fragments[0])]
        : [this.getFragmentWithoutPicturesContext(fragments[0])],
      dataviz
    );
  }
  private getFragmentWithoutPicturesContext(fragment: SearchFragmentPosts) {
    return {
      ...fragment,
      request: {
        ...fragment.request,
        query: refineClauseWithoutField(fragment.request.query, "media"),
      },
    };
  }
  private getFragmentWithPicturesContext(fragment: SearchFragmentPosts) {
    return {
      ...fragment,
      request: {
        ...fragment.request,
        query: refineClauseWithInnerClause(fragment.request.query, {
          media: ["image", "video"],
        }),
      },
    };
  }
  public createDataviz(dataviz: Dataviz) {
    if (isPostsCardDataviz(dataviz)) {
      return dataviz;
    } else {
      return getDefaultDataviz(DatavizName.TopPosts) as PostsCard["dataviz"];
    }
  }
}
